import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Typography, Alert } from 'antd';
import { LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import futezapLogo from '../assets/futezap.jpg';

const { Title, Text } = Typography;

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      message.error('Token não encontrado');
      navigate('/login');
      return;
    }
    
    validateToken();
  }, [token, navigate]);

  const validateToken = async () => {
    try {
      const response = await api.get(`/password-reset/validate-token/${token}`);
      
      if (response.data.valid) {
        setTokenValid(true);
        setTeamName(response.data.team_name);
      } else {
        message.error(response.data.message || 'Token inválido');
        navigate('/login');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Token inválido ou expirado');
      navigate('/login');
    }
    setValidating(false);
  };

  const handleResetPassword = async (values) => {
    setLoading(true);
    try {
      const response = await api.post('/password-reset/reset-password', {
        token: token,
        password: values.password
      });
      
      if (response.data.success) {
        setSuccess(true);
        message.success('Senha redefinida com sucesso!');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        message.error(response.data.message || 'Erro ao redefinir senha');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Erro ao redefinir senha');
    }
    setLoading(false);
  };

  if (validating) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #00B96B 0%, #52c41a 100%)', 
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card style={{ 
          width: '100%', 
          maxWidth: 400, 
          borderRadius: 12, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ padding: 40 }}>
            <Text>Validando token...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #00B96B 0%, #52c41a 100%)', 
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card style={{ 
          width: '100%', 
          maxWidth: 400, 
          borderRadius: 12, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ padding: 40 }}>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 16 }} />
            <Title level={3} style={{ color: '#00B96B' }}>Senha Redefinida!</Title>
            <Text>Você será redirecionado para o login em alguns segundos...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return null;
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #00B96B 0%, #52c41a 100%)', 
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Card style={{ 
        width: '100%', 
        maxWidth: 400, 
        borderRadius: 12, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)' 
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={futezapLogo} alt="Futezap" style={{ height: 60, borderRadius: 8, marginBottom: 16 }} />
          <Title level={3} style={{ color: '#00B96B', marginBottom: 8 }}>
            Nova Senha
          </Title>
          <Text style={{ color: '#666' }}>
            Time: <strong>{teamName}</strong>
          </Text>
        </div>

        <Alert
          message="Defina sua nova senha"
          description="Escolha uma senha segura com pelo menos 6 caracteres"
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleResetPassword}
        >
          <Form.Item
            name="password"
            label="Nova Senha"
            rules={[
              { required: true, message: 'Senha é obrigatória' },
              { min: 6, message: 'Senha deve ter pelo menos 6 caracteres' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />}
              placeholder="Digite sua nova senha" 
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirmar Senha"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Confirmação de senha é obrigatória' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('As senhas não coincidem'));
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />}
              placeholder="Confirme sua nova senha" 
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              size="large" 
              loading={loading}
              block
            >
              Redefinir Senha
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Button type="link" onClick={() => navigate('/login')}>
            Voltar ao Login
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ResetPassword;